import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';

const VideoPlayer = ({ 
  videoSrc = "/promo.mp4",
  title = "Modern Steel Processing",
  description = "Our advanced re-rolling mill processes raw billets through controlled heating, precise rolling, and systematic cooling to achieve optimal mechanical properties.",
  className = ""
}) => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Detect mobile device and setup intersection observer for lazy loading
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    // Wait for website to fully load before enabling video loading
    const handleLoad = () => {
      // Set up intersection observer for lazy loading
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !hasStartedLoading) {
              setIsVisible(true);
              setHasStartedLoading(true);
              generateThumbnail();
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '100px', // Start loading when video is 100px away from viewport
          threshold: 0.1
        }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    };

    const generateThumbnail = () => {
      const video = document.createElement('video');
      video.src = videoSrc;
      video.preload = 'metadata';
      video.muted = true;
      
      video.addEventListener('loadeddata', () => {
        // Seek to last frame (99% of duration)
        video.currentTime = video.duration * 0.99;
      });
      
      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        setThumbnailUrl(canvas.toDataURL());
      });
    };
    
    checkMobile();
    
    // Check if page is already loaded
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, [videoSrc, hasStartedLoading]);

  const handlePlay = () => {
    if (!isVisible) return; // Don't play if video hasn't loaded yet
    
    setIsLoading(true);
    setIsPlaying(true);
    
    if (videoRef.current) {
      // Ensure video source is set for streaming
      if (!videoRef.current.src) {
        videoRef.current.src = videoSrc;
      }
      
      // For mobile, ensure proper fullscreen handling
      if (isMobile) {
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
      }
      
      videoRef.current.play().then(() => {
        setIsLoading(false);
      }).catch((error) => {
        console.warn('Video play failed:', error);
        setIsLoading(false);
        setIsPlaying(false);
      });
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setShowControls(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }
  };

  const handleTouchStart = () => {
    if (isMobile && isPlaying) {
      setShowControls(!showControls);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative aspect-video rounded-xl overflow-hidden ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
    >
      {/* Thumbnail Image - Show placeholder until video is ready */}
      {!isPlaying && (
        <div className="absolute inset-0 w-full h-full bg-neutral-900 flex items-center justify-center">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt="Video thumbnail"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : isVisible ? (
            <div className="text-white">Loading video...</div>
          ) : (
            <div className="text-white/60">Video will load when visible</div>
          )}
        </div>
      )}

      {/* Video Element - Only render when visible and loading has started */}
      {isVisible && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover ${isPlaying ? 'block' : 'hidden'}`}
          controls={showControls || (isMobile && isPlaying)}
          preload="none"
          playsInline
          webkit-playsinline="true"
          muted={false}
          onLoadedData={() => setIsLoading(false)}
          onWaiting={() => setIsLoading(true)}
          onCanPlay={() => setIsLoading(false)}
          onPause={() => isMobile && setShowControls(true)}
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Play Button Overlay */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <motion.button
            onClick={handlePlay}
            className="w-20 h-20 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-2xl hover:bg-white transition-colors duration-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-8 h-8 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </motion.button>
        </div>
      )}

      {/* Video Info Overlay - Hide when controls are shown to prevent interference */}
      {!isPlaying && !showControls && (
        <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
          <div className="bg-black/60 backdrop-blur-md rounded-lg p-4">
            <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
            <p className="text-white/80 text-sm line-clamp-2">{description}</p>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoPlayer;